/**
 * Model template governance — the governed chain a workbook layout passes
 * before the engine trusts it (UI-04).
 *
 * `/model-change/adopt` creates the next version of a template as a DRAFT and
 * says "complete the governed chain (submit → approve → lock)". No screen
 * called the chain, so an adopted layout stayed a draft and its figures were
 * never extracted; the onboarding drafts the mapgen sweep proposed were stored
 * on the intake record and never read back. This page is that chain: every
 * version by template, its one next step, the approval discipline the platform
 * is set to (dual — four-eyes — or single), the onboarding drafts waiting on a
 * person, and the tamper-evident audit chain underneath.
 */
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { AlertTriangle, CheckCircle2, FileSpreadsheet, Loader2, Lock, ShieldCheck, Users } from 'lucide-react'

import {
  modelTemplateKeys,
  modelTemplatesService,
  nextStep,
  type ModelTemplate,
  type TemplateGovernance,
} from '@/services/modelTemplates'

function detailOf(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return typeof detail === 'string' ? detail : 'The action was refused. Please retry.'
}

const STATUS_TONE: Record<string, string> = {
  draft: 'border-stone-600 text-stone-300',
  pending_approval: 'border-amber-400/40 text-amber-200',
  approved: 'border-sky-400/40 text-sky-200',
  locked: 'border-emerald-400/40 text-emerald-200',
  retired: 'border-stone-800 text-stone-600',
}

function statusLabel(status: string): string {
  return status.replace(/_/g, ' ')
}

/** Approval discipline, in the words the Settings page uses. */
export function approvalModeLabel(governance: TemplateGovernance | undefined): string {
  if (!governance) return 'Approval mode: reading…'
  return governance.approvalMode === 'single'
    ? 'Single user — the author may approve their own version; the mode is recorded on every approval'
    : 'Dual approval (four-eyes) — the approver must differ from the version’s author'
}

export default function ModelTemplateGovernance() {
  const queryClient = useQueryClient()
  const templates = useQuery({ queryKey: modelTemplateKeys.list, queryFn: modelTemplatesService.list })
  const governance = useQuery({ queryKey: modelTemplateKeys.governance, queryFn: modelTemplatesService.governance })
  const events = useQuery({ queryKey: modelTemplateKeys.events, queryFn: modelTemplatesService.events })
  const chain = useQuery({ queryKey: modelTemplateKeys.chain, queryFn: modelTemplatesService.verifyChain })
  const [basis, setBasis] = useState<Record<string, string>>({})
  const [error, setError] = useState<{ id: string; message: string } | null>(null)

  const step = useMutation({
    mutationFn: async (input: { template: ModelTemplate; verb: 'submit' | 'approve' | 'lock' | 'retire' }) => {
      const { template, verb } = input
      if (verb === 'submit') return modelTemplatesService.submit(template.id)
      if (verb === 'approve') return modelTemplatesService.approve(template.id, (basis[template.id] ?? '').trim())
      if (verb === 'lock') return modelTemplatesService.lock(template.id)
      return modelTemplatesService.retire(template.id)
    },
    onSuccess: async (_result, input) => {
      setError(null)
      setBasis((b) => ({ ...b, [input.template.id]: '' }))
      await queryClient.invalidateQueries({ queryKey: modelTemplateKeys.all })
    },
    onError: (e: unknown, input) => setError({ id: input.template.id, message: detailOf(e) }),
  })

  // Versions grouped by template (name · client), newest version first.
  const groups = useMemo(() => {
    const byKey = new Map<string, ModelTemplate[]>()
    for (const t of templates.data ?? []) {
      const key = `${t.name} · ${t.client}`
      byKey.set(key, [...(byKey.get(key) ?? []), t])
    }
    return [...byKey.entries()].map(([key, versions]) => ({
      key,
      versions: [...versions].sort((a, b) => b.version - a.version),
    }))
  }, [templates.data])

  const drafts = governance.data?.onboardingDrafts ?? []

  return (
    <div className="space-y-4 p-4" data-testid="model-template-governance">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-amber-400/20 bg-amber-400/10">
            <ShieldCheck size={17} className="text-amber-300" />
          </div>
          <div>
            <h1 className="text-[15px] font-semibold text-stone-100">Model template governance</h1>
            <p className="text-[11px] text-stone-500">
              A workbook layout the engine extracts from passes submit → approve → lock. Nothing changes for
              parsing until a version is locked.
            </p>
          </div>
        </div>
        <div
          className="flex items-center gap-2 rounded border border-stone-800 bg-black/30 px-3 py-1.5 text-[10px] text-stone-300"
          data-testid="approval-mode"
        >
          <Users size={11} className="text-amber-300" />
          <span>{approvalModeLabel(governance.data)}</span>
          <Link to="/settings" className="text-amber-300 underline decoration-dotted underline-offset-2 hover:text-amber-200">
            Change in Settings
          </Link>
        </div>
      </div>

      <section className="panel">
        <div className="panel-header">
          <span className="panel-header-title">Templates</span>
          <span className="text-[10px] text-stone-500">
            {templates.data ? `${templates.data.length} version${templates.data.length === 1 ? '' : 's'}` : ''}
          </span>
        </div>
        <div className="panel-body space-y-3">
          {templates.isLoading ? (
            <p className="flex items-center gap-2 text-[11px] text-stone-500">
              <Loader2 size={12} className="animate-spin" /> Reading the registry…
            </p>
          ) : templates.isError ? (
            <p className="flex items-center gap-2 text-[11px] text-red-300">
              <AlertTriangle size={12} /> {detailOf(templates.error)}
            </p>
          ) : groups.length === 0 ? (
            <p className="text-[11px] text-stone-500">
              No template is registered. Adopting new lines from an intake workbook, or onboarding one below,
              creates the first draft.
            </p>
          ) : (
            groups.map((group) => (
              <div key={group.key} className="rounded-lg border border-stone-800/80">
                <div className="flex items-center gap-2 border-b border-stone-800/80 px-3 py-1.5">
                  <FileSpreadsheet size={12} className="text-amber-300" />
                  <span className="text-[11px] font-medium text-stone-200">{group.key}</span>
                </div>
                <ul className="divide-y divide-stone-800/60">
                  {group.versions.map((t) => {
                    const next = nextStep(t.status)
                    const needsBasis = next?.verb === 'approve'
                    const basisText = (basis[t.id] ?? '').trim()
                    const busy = step.isPending && step.variables?.template.id === t.id
                    return (
                      <li key={t.id} className="flex flex-wrap items-center gap-3 px-3 py-2" data-testid="template-version" data-status={t.status}>
                        <span className="font-mono text-[11px] text-stone-300">v{t.version}</span>
                        <span className={`rounded border px-1.5 py-px text-[9px] uppercase tracking-wide ${STATUS_TONE[t.status] ?? 'border-stone-700 text-stone-400'}`}>
                          {statusLabel(t.status)}
                        </span>
                        <span className="text-[10px] text-stone-500">
                          {(t.anchorMap?.anchors ?? []).length} anchor{(t.anchorMap?.anchors ?? []).length === 1 ? '' : 's'} · by {t.createdBy}
                          {t.approvedBy ? ` · approved by ${t.approvedBy}` : ''}
                        </span>
                        <span className="ml-auto flex flex-wrap items-center gap-2">
                          {needsBasis ? (
                            <input
                              value={basis[t.id] ?? ''}
                              onChange={(e) => setBasis((b) => ({ ...b, [t.id]: e.target.value }))}
                              placeholder="Basis for approval (required)"
                              aria-label={`Basis for approving v${t.version}`}
                              className="w-64 rounded border border-stone-700 bg-stone-950 px-2 py-1 text-[10px] text-stone-200"
                            />
                          ) : null}
                          {next ? (
                            <button
                              type="button"
                              disabled={busy || (needsBasis && !basisText)}
                              onClick={() => step.mutate({ template: t, verb: next.verb })}
                              className="btn-primary px-3 py-1 text-[10px] disabled:opacity-50"
                              title={needsBasis && !basisText ? 'State the basis for the approval' : undefined}
                            >
                              {busy ? <Loader2 size={10} className="inline animate-spin" /> : next.verb === 'lock' ? <Lock size={10} className="mr-1 inline" /> : null}
                              {next.label}
                            </button>
                          ) : t.status === 'locked' ? (
                            <span className="flex items-center gap-1 text-[10px] text-emerald-300">
                              <CheckCircle2 size={11} /> governs extraction
                            </span>
                          ) : null}
                          {t.status !== 'retired' ? (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => step.mutate({ template: t, verb: 'retire' })}
                              className="btn-secondary px-2 py-1 text-[10px] disabled:opacity-50"
                            >
                              Retire
                            </button>
                          ) : null}
                        </span>
                        {error?.id === t.id ? (
                          <p className="basis-full text-[10px] text-red-300" role="alert">{error.message}</p>
                        ) : null}
                      </li>
                    )
                  })}
                </ul>
              </div>
            ))
          )}
        </div>
      </section>

      <section className="panel">
        <div className="panel-header">
          <span className="panel-header-title">Onboarding drafts</span>
          <span className="text-[10px] text-stone-500">
            {governance.data
              ? `${drafts.length} waiting · ${governance.data.scannedRecords} recent record${governance.data.scannedRecords === 1 ? '' : 's'} read`
              : ''}
          </span>
        </div>
        <div className="panel-body">
          {governance.isLoading ? (
            <p className="flex items-center gap-2 text-[11px] text-stone-500"><Loader2 size={12} className="animate-spin" /> Reading…</p>
          ) : drafts.length === 0 ? (
            <p className="text-[11px] text-stone-500">
              No onboarding draft is waiting. A draft appears when a reviewer runs the onboarding scan on an
              unrecognized workbook from the intake queue.
            </p>
          ) : (
            <ul className="divide-y divide-stone-800/60">
              {drafts.map((d) => (
                <li key={`${d.ingestionId}-${d.filename}`} className="flex flex-wrap items-center gap-3 py-1.5" data-testid="onboarding-draft">
                  <FileSpreadsheet size={12} className="text-emerald-400" />
                  <span className="text-[11px] text-stone-200">{d.filename}</span>
                  <span className="text-[10px] text-stone-500">
                    {d.metrics} metric{d.metrics === 1 ? '' : 's'} proposed · {d.candidates} candidate cell{d.candidates === 1 ? '' : 's'}
                    {d.subject ? ` · ${d.subject}` : ''}
                  </span>
                  <Link
                    to={`/intake?focus=${encodeURIComponent(d.ingestionId)}`}
                    className="ml-auto text-[10px] text-amber-300 underline decoration-dotted underline-offset-2 hover:text-amber-200"
                  >
                    Open record {d.ingestionId}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="panel">
        <div className="panel-header">
          <span className="panel-header-title">Audit chain</span>
          <span className="text-[10px] text-stone-500" data-testid="chain-status">
            {chain.data ? (chain.data.ok ? 'chain intact' : `chain broken at #${chain.data.firstBrokenSeq}`) : ''}
          </span>
        </div>
        <div className="panel-body">
          {events.data && events.data.length > 0 ? (
            <ul className="max-h-64 space-y-0.5 overflow-y-auto scrollbar-thin font-mono text-[10px]">
              {events.data.map((e) => (
                <li key={e.seq} className={`flex gap-2 ${e.type === 'ACCESS_DENIED' ? 'text-red-300' : 'text-stone-400'}`}>
                  <span className="w-8 shrink-0 text-stone-600">#{e.seq}</span>
                  <span className="w-28 shrink-0">{e.type}</span>
                  <span className="min-w-0 flex-1 truncate">{e.actor}{e.templateId ? ` · ${e.templateId.slice(0, 8)}` : ''}</span>
                  <span className="shrink-0 text-stone-600">{e.at ? String(e.at).slice(0, 16).replace('T', ' ') : ''}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[11px] text-stone-500">No event on the chain yet.</p>
          )}
        </div>
      </section>
    </div>
  )
}
